import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import FormGroupRenderer from '@app/components/FormGroupRenderer'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { ISignType, SignType } from '@app/models/SignType'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Column } from 'react-virtualized'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { actionCreators, ISignTypesListState } from '../modules/signTypesList'

type ComponentProps = ISignTypesListState &
  ICurrentUserState &
  typeof actionCreators

interface IComponentState {
  addingType: boolean
  editingType: any
  showConfirmDelete: any
}

export default class SignTypesListPage extends React.PureComponent<
  ComponentProps,
  IComponentState
> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.state = {
      addingType: false,
      editingType: {},
      showConfirmDelete: {},
    }

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    const userId = this.props.user ? this.props.user.id : 0

    return (
      <DocumentTitle title='Admin - Sign Types'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <div className='d-flex flex-row justify-content-between align-items-center'>
              <h3>Sign Types</h3>
              <Button
                onClick={() => this._toggleAdd()}
                size='sm'
                color='primary'
              >
                <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                <span>Add Sign Type</span>
              </Button>
              {this._renderNewModal()}
            </div>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for a sign type?'
              size='sm'
              value={this.props.filterText}
            />
          </div>
          <div className='mt-4'>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={60}
              loadItems={this.props.loadSignTypes}
              noRowsRenderer={this._noRowsRenderer}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellDataGetter={({ rowData }) => {
                  const type = rowData as ISignType
                  return type.name
                }}
                dataKey='type'
                flexGrow={1}
                label='Name'
                width={100}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const type = rowData as ISignType
                  return (
                    <div>
                      <a
                        className='text-primary'
                        onClick={(e) => {
                          this._toggleEdit(type.id)
                        }}
                      >
                        <FontAwesomeIcon icon={['fal', 'pencil']} />
                      </a>
                      {this._renderModal(type.id, type)}
                    </div>
                  )
                }}
                dataKey='editButton'
                width={30}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const type = rowData as ISignType
                  return (
                    <div>
                      <a
                        className='text-danger'
                        onClick={() => this._toggleDelete(type.id)}
                      >
                        <FontAwesomeIcon icon={['fal', 'trash']} />
                      </a>
                      <DeleteConfirmationModal
                        isOpen={this.state.showConfirmDelete[type.id]}
                        itemName={type.name}
                        onConfirm={() => this.props.removeSignType(type)}
                        onToggleModal={() => this._toggleDelete(type.id)}
                      />
                    </div>
                  )
                }}
                dataKey='deleteButton'
                width={30}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _renderNewModal() {
    return (
      <Modal
        centered
        isOpen={this.state.addingType}
        toggle={() => this._toggleAdd()}
      >
        <ModalHeader toggle={() => this._toggleAdd()}>
          {this.props.newSignType
            ? this.props.newSignType.name
            : 'New Sign Type'}
        </ModalHeader>
        <ModalBody>
          <FormGroupRenderer
            labelText='Name'
            onChange={(e) => this.props.setSignName(e, 'new-sign')}
            value={this.props.newSignType ? this.props.newSignType.name : ''}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            outline
            onClick={() => {
              this._toggleAdd()
            }}
          >
            <FontAwesomeIcon icon={['fal', 'times']} fixedWidth /> Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => {
              this.props.editSignType(this.props.newSignType)
              this._toggleAdd()
            }}
          >
            <FontAwesomeIcon icon={['fal', 'save']} /> Save
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private _renderModal(id: string, signType?: ISignType) {
    const sign = signType || new SignType()

    return (
      <Modal
        centered
        isOpen={this.state.editingType[id]}
        toggle={() => this._toggleEdit(id)}
      >
        <ModalHeader toggle={() => this._toggleEdit(id)}>
          {sign.name || 'New Sign Type'}
        </ModalHeader>
        <ModalBody>
          <FormGroupRenderer
            labelText='Name'
            onChange={(e) => this.props.setSignName(e, id)}
            value={sign.name}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            outline
            onClick={() => this._toggleEdit(id)}
          >
            <FontAwesomeIcon icon={['fal', 'times']} fixedWidth /> Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => {
              this.props.editSignType(sign)
              this._toggleEdit(id)
            }}
          >
            <FontAwesomeIcon icon={['fal', 'save']} /> Save
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private _noRowsRenderer() {
    return <div>No sign types</div>
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }

  private _toggleAdd() {
    this.setState({ addingType: !this.state.addingType })
    this.props.clearNewType()
  }

  private _toggleEdit(id: string) {
    this.setState({ editingType: { [id]: !this.state.editingType[id] } })
  }

  private _toggleDelete(id: string) {
    this.setState({
      showConfirmDelete: { [id]: !this.state.showConfirmDelete[id] },
    })
  }
}
